import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Mention,
  Paragraph,
  Undo,
  Heading,
  Link,
  List,
  BlockQuote,
  Table,
  TableToolbar,
  Alignment,
  FontSize,
  FontFamily,
  FontColor,
  FontBackgroundColor,
  Underline,
  Strikethrough,
  SourceEditing,
  type EditorConfig,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { Box } from '@mui/material';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Enter content here...',
  error = false,
}: RichTextEditorProps) => {
  const editorConfig: EditorConfig = {
    licenseKey: 'GPL', // Use GPL license for open source
    toolbar: [
      'undo',
      'redo',
      '|',
      'sourceEditing',
      '|',
      'heading',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'link',
      'insertTable',
      'blockQuote',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
    ],
    plugins: [
      Bold,
      Essentials,
      Italic,
      Mention,
      Paragraph,
      Undo,
      Heading,
      Link,
      List,
      BlockQuote,
      Table,
      TableToolbar,
      Alignment,
      FontSize,
      FontFamily,
      FontColor,
      FontBackgroundColor,
      Underline,
      Strikethrough,
      SourceEditing,
    ],
    placeholder,
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
    },
  };

  return (
    <Box
      sx={{
        border: error ? '2px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        overflow: 'hidden',
        '&:hover': {
          borderColor: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)',
        },
        '&:focus-within': {
          borderColor: error ? '#d32f2f' : '#1976d2',
          borderWidth: '2px',
        },
        '& .ck.ck-editor': {
          width: '100%',
        },
        '& .ck-editor__editable': {
          minHeight: '400px !important',
          maxHeight: '600px',
          overflow: 'auto',
        },
        '& .ck-editor__editable_inline': {
          padding: '16px',
        },
        '& .ck.ck-toolbar': {
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <CKEditor
        editor={ClassicEditor}
        config={editorConfig}
        data={value || ''}
        onChange={(_event, editor) => {
          const data = editor.getData();
          onChange(data);
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
